// components/ui/FilterMenu.tsx — one "Filter" button for a table. It opens a two-column card: the kinds of filter on
// the left, the choices for the selected kind on the right (tick several, or one where only one makes sense).
// The card is position:fixed under the button, so a table card with few rows (overflow hidden) does not clip it.
// `input: 'dateRange'` is the When filter (lib/dates.ts presets, custom from / to, an optional hour window).
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, SlidersHorizontal, X } from 'lucide-react';
import { TableColumnsContext } from './tableColumns';
import { DATE_PRESETS } from '../../lib/dates';

export interface FilterGroup {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  /** Only one choice at a time (a date window, say). Default: several. */
  single?: boolean;
  /** The choice that means "no filter" (a view's default, say): not counted on the button, and what Clear returns to. */
  defaultValue?: string;
  /** Never counted on the button (the Columns group). */
  uncounted?: boolean;
  /** A date and a time window instead of choices: the value is [date, from, to] (YYYY-MM-DD, HH:MM, HH:MM; any may be ''). */
  input?: 'dateTime' | 'dateRange';
  /** With `input: 'dateRange'`: an hour window (From / To, 08:00–20:00) under the presets. The value is then
   *  [preset, from, to, hourFrom, hourTo] (preset a lib/dates.ts key; dates YYYY-MM-DD; hours HH:MM; '' when unset). */
  hours?: boolean;
}
const HOURS = Array.from({ length: 13 }, (_, i) => `${String(i + 8).padStart(2, '0')}:00`);
export type FilterValue = Record<string, string[]>;

export function FilterMenu({ groups: own, value: ownValue, onChange, className = '' }: { groups: FilterGroup[]; value: FilterValue; onChange: (next: FilterValue) => void; className?: string }) {
  const cols = useContext(TableColumnsContext);
  const groups = cols ? [...own, cols.group] : own;
  const value: FilterValue = cols ? { ...ownValue, [cols.group.key]: cols.value } : ownValue;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(groups[0]?.key ?? '');
  // What is ticked in the open card; it becomes `value` on Apply. Clear empties everything and applies that.
  const [draft, setDraft] = useState<FilterValue>(value);
  const show = () => { setDraft(value); setOpen(true); };
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = button.current?.getBoundingClientRect();
      if (!r) return;
      // Right-aligned under the button, but never off the left edge: on a phone the button sits at the left, so the card
      // (26rem, or the viewport less a gutter) is pulled back to keep 16px on both sides.
      const width = Math.min(26 * 16, window.innerWidth - 32);
      setPos({ top: r.bottom + 8, right: Math.min(Math.max(16, window.innerWidth - r.right), window.innerWidth - width - 16) });
    };
    place();
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const picked = (v: FilterValue, g: FilterGroup) => {
    if (g.uncounted) return 0;
    if (g.input === 'dateRange') { const [p = '', , , hf = '', ht = ''] = v[g.key] ?? []; return (p && p !== (g.defaultValue ?? '')) || hf || ht ? 1 : 0; }
    if (g.input) return (v[g.key] ?? []).some(Boolean) ? 1 : 0;
    return (v[g.key] ?? []).filter((x) => x !== g.defaultValue).length;
  };
  const count = groups.reduce((n, g) => n + picked(value, g), 0);
  const draftCount = groups.reduce((n, g) => n + picked(draft, g), 0);
  const group = groups.find((g) => g.key === active) ?? groups[0];
  const chosen = new Set(draft[group?.key ?? ''] ?? []);
  const toggle = (v: string) => {
    if (!group) return;
    const cur = draft[group.key] ?? [];
    const next = group.single ? (cur.includes(v) ? [] : [v]) : cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    setDraft({ ...draft, [group.key]: next });
  };
  const apply = () => {
    if (cols) { const { [cols.group.key]: ticked, ...rest } = draft; cols.apply(ticked ?? []); onChange(rest); } else onChange(draft);
    setOpen(false);
  };
  const clear = () => { setDraft({}); onChange({}); cols?.reset(); setOpen(false); };

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <button ref={button} type="button" onClick={() => (open ? setOpen(false) : show())} aria-haspopup="dialog" aria-expanded={open}
        className={`inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium transition ${count || open ? 'border-bronze-500 bg-bronze-50 text-bronze-600' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50 hover:text-ink-900'}`}>
        <SlidersHorizontal className="h-3.5 w-3.5" />Filter
        {count > 0 && <span className="rounded-full bg-bronze-500 px-1.5 text-[10.5px] font-semibold tabular-nums text-white">{count}</span>}
      </button>
      {open && group && (
        <div role="dialog" aria-label="Filters" style={pos ? { top: pos.top, right: pos.right } : { visibility: 'hidden' }} className="menu-pop fixed z-50 w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-ink-200 bg-white shadow-float">
          <div className="flex items-center justify-between border-b border-ink-100 px-3 py-1.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">Filter</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-md p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="grid grid-cols-[7.5rem_1fr] sm:grid-cols-[11rem_1fr]">
            {/* Left: what you can filter by. */}
            <ul className="border-r border-ink-100 bg-ink-50/50 py-1">
              {groups.map((g) => {
                const n = picked(draft, g);
                return (
                  <li key={g.key}>
                    <button type="button" onClick={() => setActive(g.key)} className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12.5px] transition ${g.key === group.key ? 'bg-white font-medium text-bronze-600' : 'text-ink-600 hover:bg-white/70 hover:text-ink-900'}`}>
                      <span className="truncate">{g.label}</span>
                      {n > 0 && <span className="rounded-full bg-bronze-500 px-1.5 text-[10.5px] font-semibold tabular-nums text-white">{n}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
            {/* Right: the choices for that kind (or a date and a time window). */}
            {group.input === 'dateRange' ? (() => {
              const cur = draft[group.key] ?? [];
              const [preset = group.defaultValue ?? '', from = '', to = '', hf = '', ht = ''] = cur;
              const setAt = (i: number, v: string) => { const next = [preset, from, to, hf, ht]; next[i] = v; setDraft({ ...draft, [group.key]: next }); };
              const sel = 'h-8 w-full rounded-lg border border-ink-200 bg-white px-2 text-[12.5px] text-ink-800';
              const presets = group.options.length ? group.options : DATE_PRESETS.map((p) => ({ value: p.key, label: p.label }));
              return (
                <div className="min-w-0">
                  <ul className="max-h-60 overflow-y-auto py-1">
                    {presets.map((o) => {
                      const on = preset === o.value;
                      return (
                        <li key={o.value}>
                          <button type="button" role="radio" aria-checked={on} onClick={() => setAt(0, o.value)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-ink-800 transition hover:bg-ink-50">
                            <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border transition ${on ? 'border-bronze-500 bg-bronze-500 text-white' : 'border-ink-300 bg-white'}`}>{on && <Check className="h-3 w-3" />}</span>
                            <span className="truncate">{o.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {(preset === 'custom' || group.hours) && (
                    <div className="space-y-2.5 border-t border-ink-100 p-3">
                      {preset === 'custom' && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block text-[11.5px] font-medium text-ink-500">From<input type="date" value={from} max={to || undefined} onChange={(e) => setAt(1, e.target.value)} className={`mt-1 ${sel}`} /></label>
                          <label className="block text-[11.5px] font-medium text-ink-500">To<input type="date" value={to} min={from || undefined} onChange={(e) => setAt(2, e.target.value)} className={`mt-1 ${sel}`} /></label>
                        </div>
                      )}
                      {group.hours && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block text-[11.5px] font-medium text-ink-500">From hour<select value={hf} onChange={(e) => setAt(3, e.target.value)} className={`mt-1 ${sel}`}><option value="">Any</option>{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
                          <label className="block text-[11.5px] font-medium text-ink-500">To hour<select value={ht} onChange={(e) => setAt(4, e.target.value)} className={`mt-1 ${sel}`}><option value="">Any</option>{HOURS.filter((h) => !hf || h > hf).map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })() : group.input === 'dateTime' ? (() => {
              const [d = '', from = '', to = ''] = draft[group.key] ?? [];
              const setAt = (i: number, v: string) => { const next = [d, from, to]; next[i] = v; setDraft({ ...draft, [group.key]: next }); };
              const sel = 'h-8 w-full rounded-lg border border-ink-200 bg-white px-2 text-[12.5px] text-ink-800';
              return (
                <div className="space-y-2.5 p-3">
                  <label className="block text-[11.5px] font-medium text-ink-500">Date<input type="date" value={d} onChange={(e) => setAt(0, e.target.value)} className={`mt-1 ${sel}`} /></label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-[11.5px] font-medium text-ink-500">From<select value={from} onChange={(e) => setAt(1, e.target.value)} className={`mt-1 ${sel}`}><option value="">Any</option>{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
                    <label className="block text-[11.5px] font-medium text-ink-500">To<select value={to} onChange={(e) => setAt(2, e.target.value)} className={`mt-1 ${sel}`}><option value="">Any</option>{HOURS.filter((h) => !from || h > from).map((h) => <option key={h} value={h}>{h}</option>)}</select></label>
                  </div>
                </div>
              );
            })() : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {group.options.map((o) => {
                const on = chosen.has(o.value);
                return (
                  <li key={o.value}>
                    <button type="button" role={group.single ? 'radio' : 'checkbox'} aria-checked={on} onClick={() => toggle(o.value)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-ink-800 transition hover:bg-ink-50">
                      <span className={`grid h-4 w-4 shrink-0 place-items-center border transition ${group.single ? 'rounded-full' : 'rounded-[4px]'} ${on ? 'border-bronze-500 bg-bronze-500 text-white' : 'border-ink-300 bg-white'}`}>{on && <Check className="h-3 w-3" />}</span>
                      <span className="truncate" title={o.label}>{o.label}</span>
                    </button>
                  </li>
                );
              })}
              {!group.options.length && <li className="px-4 py-6 text-center text-[12px] text-ink-400">Nothing to choose</li>}
            </ul>
            )}
          </div>
          <div className="flex items-center justify-end gap-1.5 border-t border-ink-100 bg-ink-50/50 px-3 py-2">
            <button type="button" onClick={clear} disabled={!count && !draftCount} className="h-7 rounded-full border border-ink-200 bg-ink-100 px-3 text-[12px] font-medium text-ink-800 transition hover:bg-ink-200 disabled:opacity-40">Clear</button>
            <button type="button" onClick={apply} className="h-7 rounded-full border border-bronze-500 bg-bronze-500 px-3 text-[12px] font-medium text-white transition hover:border-bronze-600 hover:bg-bronze-600">Apply{draftCount ? ` (${draftCount})` : ''}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A group for `ListFilter`: one list filter key, its choices, and how the chosen string is stored on the list. */
export interface ListFilterGroup { key: string; label: string; options: Array<{ value: string; label: string }>; /** Store as a number or a boolean instead of a string. */ as?: 'number' | 'boolean'; /** Tick several; they are sent comma-joined (the API must accept that). */ multi?: boolean; /** The choice that means "no filter"; not counted, and what Clear returns to. */ defaultValue?: string }

/** The Filter button wired to a `useList` (one choice per filter key, the way the API filters work). */
export function ListFilter({ filters, setFilters, groups, className }: { filters: Record<string, unknown>; setFilters: (next: Record<string, unknown>) => void; groups: ListFilterGroup[]; className?: string }) {
  const value: FilterValue = Object.fromEntries(groups.map((g) => { const v = filters[g.key]; return [g.key, v === undefined || v === '' || v === null ? [] : g.multi ? String(v).split(',').filter(Boolean) : [String(v)]]; }));
  const onChange = (next: FilterValue) => {
    const patch: Record<string, unknown> = { ...filters };
    for (const g of groups) {
      const picked = next[g.key] ?? [];
      const raw = g.multi ? (picked.length ? picked.join(',') : undefined) : picked[0];
      patch[g.key] = raw === undefined ? undefined : g.as === 'number' ? Number(raw) : g.as === 'boolean' ? raw === 'true' : raw;
    }
    setFilters(patch);
  };
  return <FilterMenu className={className} value={value} onChange={onChange} groups={groups.map((g) => ({ key: g.key, label: g.label, options: g.options, single: !g.multi, defaultValue: g.defaultValue }))} />;
}
